import React from 'react';
import { Position, Checkbox, InputGroup } from '@blueprintjs/core';
import { LocalizedDateInput as DateInput } from '@/components/Forms/LocalizedDateInputs';
import moment from 'moment';
import intl from 'react-intl-universal';
import { isUndefined } from 'lodash';

import { useAutofocus } from '@/hooks';
import { T } from '@/components/FormattedMessage';
import { Choose } from '@/components/Utils/Choose';
import { ListSelect } from '@/components/Select/ListSelect';
import { momentFormatter } from '@/utils';

function AdvancedFilterEnumerationField({
  options,
  value,
  ...rest
}: {
  options?: any[];
  value?: any;
  [key: string]: any;
}) {
  return (
    <ListSelect
      items={options}
      selectedItem={value}
      popoverProps={{
        fill: true,
        inline: true,
        minimal: true,
        captureDismiss: true,
      }}
      defaultText={<T id={'filter.select_option'} />}
      textProp={'label'}
      selectedItemProp={'key'}
      {...rest}
    />
  );
}

const IFieldType = {
  ENUMERATION: 'enumeration',
  BOOLEAN: 'boolean',
  NUMBER: 'number',
  DATE: 'date',
};

function tansformDateValue(
  date: any,
  defaultValue: Date | null = null,
): Date | null {
  return date ? moment(date).toDate() : defaultValue;
}
/**
 * Advanced filter value field detarminer.
 */
interface AdvancedFilterValueFieldProps {
  value?: any;
  fieldType?: string;
  options?: any[];
  onChange?: (value: any) => void;
  isFocus?: boolean;
  label?: React.ReactNode;
}

export default function AdvancedFilterValueField2({
  value,
  fieldType,
  options,
  onChange,
  isFocus,
}: AdvancedFilterValueFieldProps) {
  const [localValue, setLocalValue] = React.useState(value);

  React.useEffect(() => {
    if (localValue !== value && !isUndefined(value)) {
      setLocalValue(value);
    }
  }, [localValue, value]);

  // Input field reference.
  const valueRef = useAutofocus<HTMLInputElement>(isFocus);

  const triggerOnChange = (value: any) => onChange && onChange(value);

  // Handle input change.
  const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    if (e.currentTarget.type === 'checkbox') {
      setLocalValue(e.currentTarget.checked);
      triggerOnChange(e.currentTarget.checked);
    } else {
      setLocalValue(e.currentTarget.value);
      triggerOnChange(e.currentTarget.value);
    }
  };

  // Handle enumeration field type change.
  const handleEnumerationChange = (option: any) => {
    setLocalValue(option.key);
    triggerOnChange(option.key);
  };

  // Handle date field change.
  const handleDateChange = (date: any) => {
    const formattedDate = moment(date).format('YYYY/MM/DD');

    setLocalValue(formattedDate);
    triggerOnChange(formattedDate);
  };

  return (
    <Choose>
      <Choose.When condition={fieldType === IFieldType.ENUMERATION}>
        <AdvancedFilterEnumerationField
          options={options}
          value={localValue}
          onItemSelect={handleEnumerationChange}
        />
      </Choose.When>

      <Choose.When condition={fieldType === IFieldType.DATE}>
        <DateInput
          {...momentFormatter('YYYY/MM/DD')}
          value={tansformDateValue(localValue)}
          onChange={handleDateChange}
          popoverProps={{
            minimal: true,
            position: Position.BOTTOM,
          }}
          shortcuts={true}
          placeholder={intl.get('filter.enter_date')}
          fill={true}
          inputProps={{
            fill: true,
          }}
        />
      </Choose.When>

      <Choose.When condition={fieldType === IFieldType.BOOLEAN}>
        <Checkbox value={localValue} onChange={handleInputChange} />
      </Choose.When>

      <Choose.Otherwise>
        <InputGroup
          placeholder={intl.get('filter.value')}
          onChange={handleInputChange}
          value={localValue}
          inputRef={valueRef}
        />
      </Choose.Otherwise>
    </Choose>
  );
}
